import { Component, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AssetManagement, Header } from '@aptsi-types';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { AssetManagementReportService } from './reports.service';

@Component({
  selector: 'app-reports',
  templateUrl: './reports.component.html',
  styleUrls: ['./reports.component.scss'],
})
export class ReportsComponent implements OnInit, OnDestroy {
  assetSourceSummaryReport: AssetManagement[] = [];
  assetLifeCycleReport: AssetManagement[] = [];
  topAssetSourceByAssetCountReport: AssetManagement[] = [];
  chartdata: AssetManagement[] = [];

  sanitizedPdfUrl: SafeResourceUrl | null = null;
  isView = false;
  isLoadingReports = false;
  isLoadingChart = false;
  reportError = '';
  chartError = '';

  private pdfBlobUrl: string | null = null;

  readonly filterOptions = [
    { label: 'Last 7 days', value: 'last_7_days' },
    { label: 'Last month', value: 'last_month' },
    { label: 'All time', value: 'all_time' },
  ];

  selectedFilter = 'all_time';

  readonly headers: {
    header_1: Header[];
    header_2: Header[];
    header_3: Header[];
  } = {
    header_1: [
      { name: 'Asset Source Name', property: 'assetSourceName' },
      { name: 'Asset Type', property: 'assetType' },
      { name: 'Asset Source', property: 'assetSource' },
      { name: 'Asset Count', property: 'capturedAssetCount' },
    ],
    header_2: [
      { name: 'Asset Name', property: 'assetName' },
      { name: 'Asset Type', property: 'assetType' },
      { name: 'Asset Source', property: 'assetSource' },
      { name: 'Asset Status', property: 'assetStatus' },
      { name: 'Asset Lifecycle Status', property: 'assetLifeCycleStatus' },
    ],
    header_3: [
      { name: 'Asset Source', property: 'assetSource' },
      { name: 'Asset Type', property: 'assetType' },
      { name: 'Asset Count', property: 'capturedAssetCount' },
    ],
  };

  chartData: any = { labels: [], datasets: [] };

  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          color: '#4b5870',
          usePointStyle: true,
          boxWidth: 8,
          padding: 18,
        },
      },
      tooltip: { enabled: true, padding: 10, displayColors: true },
    },
    scales: {
      x: {
        title: { display: true, text: 'Date', color: '#6f7b91' },
        ticks: { color: '#6f7b91', maxRotation: 0, autoSkip: true },
        grid: { color: 'rgba(223, 228, 236, 0.55)' },
        border: { display: false },
      },
      y: {
        title: { display: true, text: 'Percentage of Up Assets', color: '#6f7b91' },
        min: 0,
        max: 100,
        ticks: { color: '#6f7b91', callback: (value: number) => `${value}%` },
        grid: { color: 'rgba(223, 228, 236, 0.7)' },
        border: { display: false },
      },
    },
  };

  constructor(
    private readonly assetManagementReportService: AssetManagementReportService,
    private readonly sanitizer: DomSanitizer
  ) {}

  ngOnInit(): void {
    void this.fetchReportData();
    void this.fetchChartData();
  }

  ngOnDestroy(): void {
    this.revokePdfBlobUrl();
  }

  /** Runs all reports for the chosen date range, keeping table and chart filters aligned. */
  onFilterChange(filter: string): void {
    if (!filter) {
      return;
    }

    this.selectedFilter = filter;
    void this.fetchReportData(filter);
    void this.fetchChartData(filter);
  }

  async fetchReportData(filter?: string): Promise<void> {
    if (filter) {
      this.selectedFilter = filter;
    }

    this.isLoadingReports = true;
    this.reportError = '';

    try {
      const response = await this.assetManagementReportService
        .getAmReports(this.selectedFilter)
        .toPromise();
      const payload = (response ?? {}) as Record<string, any>;

      this.assetSourceSummaryReport = Array.isArray(payload['assetSourceSummaryReport'])
        ? payload['assetSourceSummaryReport']
        : [];
      this.assetLifeCycleReport = Array.isArray(payload['assetLifeCycleReport'])
        ? payload['assetLifeCycleReport']
        : [];
      this.topAssetSourceByAssetCountReport = Array.isArray(payload['topAssetSourceByAssetCountReport'])
        ? payload['topAssetSourceByAssetCountReport']
        : [];
    } catch (error) {
      console.error('Error fetching report data:', error);
      this.reportError = 'Unable to load the reports. Check your connection and try again.';
      this.assetSourceSummaryReport = [];
      this.assetLifeCycleReport = [];
      this.topAssetSourceByAssetCountReport = [];
    } finally {
      this.isLoadingReports = false;
    }
  }

  async fetchChartData(filter?: string): Promise<void> {
    if (filter) {
      this.selectedFilter = filter;
    }

    this.isLoadingChart = true;
    this.chartError = '';

    try {
      const response = await this.assetManagementReportService
        .getAmDashboardChartData(this.selectedFilter)
        .toPromise();
      const payload = (response ?? {}) as Record<string, any>;
      this.chartdata = Array.isArray(payload['lineChartDTOs']) ? payload['lineChartDTOs'] : [];

      this.chartData = {
        labels: this.chartdata.map(item => item.lastCheckedAt ?? ''),
        datasets: [
          {
            label: 'Percentage of Up Assets',
            data: this.chartdata.map(item => Number(item.upAssets ?? 0)),
            borderColor: '#2850ad',
            backgroundColor: 'rgba(40, 80, 173, 0.12)',
            borderWidth: 2.5,
            pointRadius: 3,
            pointHoverRadius: 5,
            pointBackgroundColor: '#ffffff',
            pointBorderColor: '#2850ad',
            pointBorderWidth: 2,
            fill: true,
            tension: 0.32,
          },
        ],
      };
    } catch (error) {
      console.error('Error fetching report chart data:', error);
      this.chartError = 'Unable to load the verification trend.';
      this.chartdata = [];
      this.chartData = { labels: [], datasets: [] };
    } finally {
      this.isLoadingChart = false;
    }
  }

  async generateAndDownloadPDF(): Promise<void> {
    await this.fetchReportData();
    if (!this.hasReportData) {
      return;
    }

    this.createPDFDocument(
      this.assetSourceSummaryReport,
      this.assetLifeCycleReport,
      this.topAssetSourceByAssetCountReport
    ).save('AssetManagementReport.pdf');
  }

  async generateAndViewPDF(): Promise<void> {
    await this.fetchReportData();
    if (!this.hasReportData) {
      return;
    }

    this.revokePdfBlobUrl();
    const doc = this.createPDFDocument(
      this.assetSourceSummaryReport,
      this.assetLifeCycleReport,
      this.topAssetSourceByAssetCountReport
    );
    this.pdfBlobUrl = URL.createObjectURL(doc.output('blob'));
    this.sanitizedPdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.pdfBlobUrl);
    this.isView = true;
  }

  get selectedFilterLabel(): string {
    return this.filterOptions.find(option => option.value === this.selectedFilter)?.label ?? 'All time';
  }

  get hasReportData(): boolean {
    return this.assetSourceSummaryReport.length > 0 ||
      this.assetLifeCycleReport.length > 0 ||
      this.topAssetSourceByAssetCountReport.length > 0;
  }

  close(): void {
    this.isView = false;
    this.sanitizedPdfUrl = null;
    this.revokePdfBlobUrl();
  }

  private revokePdfBlobUrl(): void {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  private createPDFDocument(
    assetSourceSummaryReport: AssetManagement[],
    assetLifeCycleReport: AssetManagement[],
    topAssetSourceByAssetCountReport: AssetManagement[]
  ): jsPDF {
    const doc = new jsPDF();
    const brandBlue: [number, number, number] = [14, 46, 128];
    const tableOptions = {
      theme: 'striped',
      headStyles: { fillColor: brandBlue, textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
      margin: { left: 14, right: 14 },
    };

    doc.setTextColor(7, 17, 46);
    doc.setFontSize(18);
    doc.text('Asset Management Report', 14, 15);
    doc.setFontSize(9);
    doc.setTextColor(111, 123, 145);
    doc.text(`Period: ${this.filterOptions.find(item => item.value === this.selectedFilter)?.label ?? 'All time'}`, 14, 22);

    doc.setTextColor(23, 32, 51);
    doc.setFontSize(13);
    doc.text('Asset Source Summary', 14, 32);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 37,
      head: [['Asset Source Name', 'Asset Type', 'Asset Source', 'Asset Count']],
      body: assetSourceSummaryReport.map(item => [
        item.assetSourceName ?? '', item.assetType ?? '', item.assetSource ?? '', item.capturedAssetCount ?? '',
      ]),
    });

    doc.addPage();
    doc.setTextColor(23, 32, 51);
    doc.setFontSize(13);
    doc.text('Asset Lifecycle', 14, 20);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 25,
      head: [['Asset Name', 'Asset Type', 'Asset Status', 'Asset Lifecycle Status']],
      body: assetLifeCycleReport.map(item => [
        item.assetName ?? '', item.assetType ?? '', item.assetStatus ?? '', item.assetLifeCycleStatus ?? '',
      ]),
    });

    doc.addPage();
    doc.setTextColor(23, 32, 51);
    doc.setFontSize(13);
    doc.text('Top Asset Sources by Asset Count', 14, 20);
    (doc as any).autoTable({
      ...tableOptions,
      startY: 25,
      head: [['Asset Source', 'Asset Type', 'Asset Count']],
      body: topAssetSourceByAssetCountReport.map(item => [
        item.assetSource ?? '', item.assetType ?? '', item.capturedAssetCount ?? '',
      ]),
    });

    return doc;
  }
}
